import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Attachment, catchRenderException, DialogServiceProvider, DownloadService, Endpoint, generateReport, getReportsByGroup, ToastService } from '@philips-emr/hpms-provider-commons';
import { ActionAPI, DeckAPI, FetchAPI, FieldAPI, FormAPI, Image, ModalAPI, PanelAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSelect, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { CloseEvent, FetchEvent, MountEvent, RenderEvent, SelectEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ActionPlanDetailsAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { ActionPlanData, ActionPlanStatus, getStampStatusActionPlan } from './action-plan-controller';

import './action-plan.css';

const ACTION_PLAN_GROUP = '36';

@Controller({ fragment: FragmentNames.ACTION_PLAN_DETAILS })
class ActionPlanDetailsController extends BaseController {

  @Inject(Aliases.PANEL_MODAL)
  private panelModal: PanelAPI;
  @Inject(Aliases.PANEL_MODAL_HISTORY)
  private panelHistory: PanelAPI;
  @Inject(Aliases.PANEL_MODAL_ADD_HISTORY)
  private panelAddHistory: PanelAPI;
  @Inject(Aliases.DECK_ACTIONS)
  private deckActions: DeckAPI;
  @Inject(Aliases.DECK_HISTORIES)
  private deckHistories: DeckAPI;
  @Inject(Aliases.ACTION_ADD_HISTORY)
  private actionAddHistory: ActionAPI;
  @Inject(Aliases.FETCH_HISTORIES)
  private fetchHistories: FetchAPI;
  @Inject(Aliases.FIELD_ATTACHMENT_LIST)
  private fieldAttachmentsList: FieldAPI;
  @Inject(Aliases.FETCH_ACTION_PLAN)
  private fetchActionPlan: FetchAPI;
  @Inject(Aliases.ACTION_FINISH)
  private actionFinish: ActionAPI;
  @Inject(Aliases.FIELD_CANCEL_REASON)
  private fieldCancelReason: FieldAPI;
  @Inject(Aliases.FIELD_REFUSAL_REASON)
  private fieldRefusalReason: FieldAPI;
  @Inject(Aliases.FIELD_EFFECTIVITY)
  private fieldEffectivity: FieldAPI;

  @OnMount([Aliases.FETCH_ACTION_PLAN, Aliases.FETCH_ACTIONS])
  onMountFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqActionPlan } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqActionPlan });
    api.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_ACTION_PLAN)
  onAfterFetchActionPlan(event: FetchEvent): void {
    const data = event.data;
    if (data) {
      const { ieStatus, effectivity } = data as ActionPlanData;
      this.actionFinish.setEnabled(ieStatus == ActionPlanStatus.ONGOING);
      this.fieldCancelReason.setVisible(ieStatus == ActionPlanStatus.CANCELED);
      this.fieldRefusalReason.setVisible(ieStatus == ActionPlanStatus.DENIED);
      this.fieldEffectivity.setVisible(!isEmptyArrayOrObject(effectivity));
      this.context.set({ ieStatus });
    }
  }

  @OnMount(Aliases.ACTION_ADD_HISTORY)
  onMountAddHistory(event: MountEvent): void {
    const api = event.target as ActionAPI;
    const { ieStatus } = this.context.getBulkData() as unknown as ActionPlanData;
    api.setEnabled(ieStatus == ActionPlanStatus.ONGOING);
  }

  @OnMount(Aliases.ACTION_PRINT)
  onMountPrint(event: MountEvent): void {
    const api = event.target as ActionAPI;
    getReportsByGroup(ACTION_PLAN_GROUP).then((response) => {
      api.setEnabled(!isEmptyArrayOrObject(response));
    }).catch((error) => ToastService.open(error));
  }

  @OnDynamicContentRender(Aliases.FIELD_STATUS)
  onRenderStatusActionPlan(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as ActionPlanData;
    if (ieStatus) {
      const content = (): ReactElement => (getStampStatusActionPlan(ieStatus, dsStatus));
      render(content);
    }
  }

  @OnSelectItem(Aliases.DECK_ACTIONS)
  onSelectAction(event: SelectItemEvent<ActionPlanData>): void {
    const { nrSeqActionPlanItem } = event.item;
    this.context.set({ nrSeqActionPlanItem });
    this.panelModal.setVisible(true);
    this.deckActions.unselectAll();
  }

  @OnMount([
    Aliases.FETCH_ACTION, Aliases.FETCH_HISTORIES,
    Aliases.FETCH_ACTION_PLAN_HISTORY, Aliases.FETCH_ATTACHMENT_LIST])
  onMountFetchAction(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqActionPlanItem, nrSeqActionPlanHistory } = this.context.getBulkData();
    const { nrSeqActionPlan } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqActionPlan, nrSeqActionPlanItem });

    if (api.getAlias() == Aliases.FETCH_ACTION_PLAN_HISTORY || api.getAlias() == Aliases.FETCH_ATTACHMENT_LIST) {
      api.setParameters({ nrSeqActionPlan, nrSeqActionPlanHistory });
    }
    api.forceFetch();
  }

  @OnClose(Aliases.MODAL_ACTION)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
  }

  @OnSelectItem(Aliases.DECK_HISTORIES)
  onSelectHistory(event: SelectItemEvent<ActionPlanData>): void {
    const { nrSeqActionPlanHistory } = event.item;
    const api = event.target as unknown as DeckAPI;
    api.unselectAll();
    this.context.set({ nrSeqActionPlanHistory });
    this.panelHistory.setVisible(true);
  }

  @OnClick(Aliases.ACTION_ADD_HISTORY)
  onClickAddHistory(): void {
    this.panelAddHistory.setVisible(true);
  }

  @OnClose([Aliases.MODAL_HISTORY, Aliases.MODAL_NEW_HISTORY])
  onCloseModals(event: CloseEvent): void {
    const api = event.target as ModalAPI;
    if (api.getAlias() == Aliases.MODAL_HISTORY) {
      this.panelHistory.setVisible(false);
      this.deckHistories.unselectAll();
      return;
    }
    this.panelAddHistory.setVisible(false);
  }

  @OnCancel(Aliases.FORM_NEW_HISTORY)
  onCancelNewHistory(): void {
    this.panelAddHistory.setVisible(false);
  }

  @OnSelect(Aliases.ATTACHMENTS_LIST)
  onSelectAttachment(event: SelectEvent<Attachment>): void {
    const { nrSeqAttachment, filename } = event.value;
    const { nrSeqActionPlan } = this.historyService.getCurrentRouteParams();
    const { nrSeqActionPlanHistory } = this.context.getBulkData();

    DownloadService.of({
      endpoint: Endpoint.GET.DOWNLOAD_ATTACHMENT_ACTION_PLAN,
      name: filename,
      parameters: { nrSeqActionPlan, nrSeqAttachment, nrSeqActionPlanHistory },
    });
  }

  @OnAfterFetch(Aliases.FETCH_HISTORIES)
  onAfterFetchHistories(event: FetchEvent): void {
    const data = event.data as ActionPlanData[];
    if (data && data.length > 0) {
      const { statusActionPlan } = data[0];
      this.actionAddHistory.setEnabled(statusActionPlan == ActionPlanStatus.ONGOING);
    }
  }

  @OnSubmit(Aliases.FORM_NEW_HISTORY)
  onSubmitAddHistory(event: SubmitEvent): void {
    const api = event.target as FormAPI;
    const record = api.getRecord();
    const { nrSeqActionPlan } = this.historyService.getCurrentRouteParams();

    HttpService.post<Record<string, Value>>(Endpoint.POST.ACTION_PLAN_HISTORY, { }, { body: { ...record, nrSeqActionPlan } })
      .then((response) => {
        this.panelAddHistory.setVisible(false);
        ToastService.open(response);
        this.fetchHistories.setParameters({ nrSeqActionPlan });
        this.fetchHistories.forceFetch();
      }).catch((error) => ToastService.open(error));
  }

  @OnDynamicContentRender(Aliases.CARD_HISTORY)
  onRenderCardHistory(event: RenderEvent): void {
    const { render, data } = event;
    const { ieAttachment } = data as ActionPlanData;
    if (ieAttachment == 'S') {
      const content = (): ReactElement => <Image alt="" src="/static/assets/DLS_Attachment_32.svg" className="icon-attachment" />;
      render(content);
    }
  }

  @OnAfterFetch(Aliases.FETCH_ATTACHMENT_LIST)
  onMountFieldAttachments(event: FetchEvent): void {
    this.fieldAttachmentsList.setVisible(!isEmptyArrayOrObject(event.data));
  }

  @OnClick(Aliases.ACTION_FINISH)
  onClickFinish(): void {
    DialogServiceProvider.confirm(MessageService.translate('provider.confirm-finish-action-plan'))
      .then(() => {
        const { nrSeqActionPlan } = this.historyService.getCurrentRouteParams();
        HttpService.post(Endpoint.POST.FINISH_ACTION_PLAN, { nrSeqActionPlan })
          .then(() => {
            this.fetchActionPlan.setParameters({ nrSeqActionPlan });
            this.fetchActionPlan.forceFetch();
            catchRenderException(() => {
              this.fetchHistories.setParameters({ nrSeqActionPlan });
              this.fetchHistories.forceFetch();
            });

            ToastService.open({ message: MessageService.translate('provider.request-sucessfull'), level: ToastLevel.SUCCESS });
          }).catch((response) => ToastService.open(response));
      });
  }

  @OnClick(Aliases.ACTION_PRINT)
  onClickActionPrint(): void {
    const { nrSeqActionPlan } = this.historyService.getCurrentRouteParams();
    const requestParams = { nrSeqActionPlan };
    generateReport(Endpoint.POST.REPORT_ACTION_PLAN, requestParams, ACTION_PLAN_GROUP)
      .finally(() => this.changeCursor(false));
  }

  private changeCursor(isProgress: boolean): void {
    document.body.style.cursor = isProgress ? 'wait' : 'default';
  }

}

export {
  ActionPlanDetailsController,
};
